import { FC, useState, KeyboardEvent } from 'react'
import { toast } from 'react-toastify'
import { ReactComponent as CloseIcon } from 'src/assets/svg/close.svg'
import { MAX_IRRELEVANT_KEYWORD_LENGTH } from 'src/constants/campaigns'
import en from 'src/constants/en'
import Button from 'src/components/Button'
import styles from './irrelevantKeywordsInput.module.scss'

interface IrrelevantKeywordsInputProps {
  existingKeywords?: string[]
  existingTags?: string[]
  irrelevantKeywords: string[]
  onAddKeyword: (keyword: string) => void
  onRemoveKeyword: (keyword: string) => void
}

export const IrrelevantKeywordsInput: FC<IrrelevantKeywordsInputProps> = ({
  irrelevantKeywords,
  onAddKeyword,
  onRemoveKeyword,
  existingKeywords = [],
}) => {
  const [currentInput, setCurrentInput] = useState<string>('')

  const validateAndAddKeyword = () => {
    const keyword = currentInput.trim()

    if (!keyword) {
      toast.error(en.IRRELEVANT_KEYWORD_ERROR_EMPTY)
      return
    }

    if (keyword.length > MAX_IRRELEVANT_KEYWORD_LENGTH) {
      toast.error(en.IRRELEVANT_KEYWORD_ERROR_TOO_LONG)
      return
    }

    const lowerCaseKeyword = keyword.toLowerCase()

    // Check if already exists in irrelevant keywords
    if (irrelevantKeywords.some((k) => k.toLowerCase() === lowerCaseKeyword)) {
      toast.error(en.IRRELEVANT_KEYWORD_ERROR_DUPLICATE)
      return
    }

    // Check if conflicts with regular keywords
    if (existingKeywords.some((k) => k?.toLowerCase() === lowerCaseKeyword)) {
      toast.error(en.IRRELEVANT_KEYWORD_ERROR_CONFLICTS_WITH_KEYWORD)
      return
    }

    // No limit check anymore - removed as requested

    onAddKeyword(keyword)
    setCurrentInput('')
    toast.success(en.IRRELEVANT_KEYWORD_SUCCESS_ADDED)
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      validateAndAddKeyword()
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.labelWrapper}>
        <span className={styles.label}>{en.irrelevantKeywords}</span>
        <span className={styles.counter}>
          {irrelevantKeywords.length}&nbsp;{en.keyWordsAdded}
        </span>
      </div>

      <div className={styles.inputSection}>
        <input
          className={styles.input}
          onChange={(e) => setCurrentInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={en.enterIrrelevantKeyword}
          type="text"
          value={currentInput}
        />
        <Button
          className={styles.addButton}
          onClick={validateAndAddKeyword}
          type="button"
        >
          {en.add}
        </Button>
      </div>

      {irrelevantKeywords.length > 0 && (
        <div className={styles.keywordsList}>
          {irrelevantKeywords.map((keyword) => (
            <div className={styles.keywordTag} key={keyword}>
              <span className={styles.keywordText}>{keyword}</span>
              <button
                className={styles.removeButton}
                onClick={() => onRemoveKeyword(keyword)}
                type="button"
              >
                <CloseIcon className={styles.closeIcon} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
